import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { format, subDays, differenceInDays, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { PageHeader } from "@/components/layout/page-header";
import { PageToolbar } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { PageSkeleton } from "@/components/data/page-skeleton";
// Espelha server/lib/equipe/radar-aplicabilidade.ts — só o tipo, sem lógica: o
// client nunca decide COM_REGISTRO/SEM_REGISTRO/NAO_SE_APLICA, só renderiza o
// que o servidor já classificou.
interface CulturaResumo {
  id: string;
  nome: string;
}
type CelulaRadar =
  | { estado: "COM_REGISTRO"; ultimaData: string }
  | { estado: "SEM_REGISTRO" }
  | { estado: "NAO_SE_APLICA" };

interface SetorProdutivoRadar {
  setorId: string;
  setorNome: string;
  culturaSetor: CulturaResumo | null;
  celulas: Record<string, CelulaRadar>;
}

interface SetorServicoRadar {
  setorId: string;
  setorNome: string;
  celulas: Record<string, CelulaRadar>;
}

// Espelha server/lib/equipe/custo-relatorio.ts. Nenhum número é recalculado aqui:
// o rateio por área, o custo-dia do mensalista e os percentuais saem prontos do
// servidor. Um segundo caminho de cálculo no client é como os dois divergem depois.
type TipoBucket = "CULTURA" | "ESTRUTURA" | "SEM_CULTURA" | "NAO_ATRIBUIDO";

interface BucketCultura {
  id: string;
  nome: string;
  tipo: TipoBucket;
  total: number;
  pctTotal: number;
  /** Só entre buckets de CULTURA — é o número do rateio contábil da quinzena. */
  pctEntreCulturas: number | null;
}

interface RelatorioData {
  totais: {
    trabalho: number;
    adicionais: number;
    fixoMensalistas: number;
    descontos: number;
    custoTotal: number;
  };
  totalTarefas: number;
  /** O período pedido vai além de hoje — o fixo foi apurado só até hoje. */
  mesCorrenteParcial: boolean;
  porCultura: BucketCultura[];
  porFuncionario: { id: string; nome: string; total: number; fixo: number; adicionais: number }[];
  porAtividade: { id: string; nome: string; total: number; qtd: number }[];
  porSetor: { id: string; nome: string; total: number; qtd: number }[];
  atividades: { id: string; nome: string }[];
  produtivos: SetorProdutivoRadar[];
  servico: SetorServicoRadar[];
  /** Compat com clientes antigos — igual a totais.trabalho. */
  totalPeriodo: number;
}

function diasAtras(dataStr: string): number {
  return differenceInDays(new Date(), parseISO(dataStr));
}

function RecenciaBadge({ dataStr }: { dataStr: string }) {
  const d = diasAtras(dataStr);
  const cls = d <= 7 ? "text-sucesso" : d <= 14 ? "text-atencao" : "text-erro";
  return <span className={`text-xs font-medium ${cls}`}>{d}d atrás</span>;
}

/** Célula da matriz de PRODUTIVOS — os 3 estados, cada um com sua própria leitura visual. */
function CelulaMatriz({ celula, culturaSetor }: { celula: CelulaRadar | undefined; culturaSetor: CulturaResumo | null }) {
  if (!celula || celula.estado === "SEM_REGISTRO") {
    return <span className="text-muted-foreground/40" title="Sem registro no app">—</span>;
  }
  if (celula.estado === "NAO_SE_APLICA") {
    return (
      <span
        className="text-muted-foreground/25"
        title={`Não se aplica a ${culturaSetor ? culturaSetor.nome.toLowerCase() : "esta cultura"}`}
      >
        ·
      </span>
    );
  }
  return <RecenciaBadge dataStr={celula.ultimaData} />;
}

function fmt(val?: number) {
  return (val ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function pctFmt(val: number) {
  return `${val.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

/** Chip de uma natureza do custo. Descontos entram com o sinal na frente, nunca somados. */
function ChipNatureza({ rotulo, valor, negativo }: { rotulo: string; valor: number; negativo?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-md border bg-background px-2 py-1 text-xs">
      <span className="text-muted-foreground">{rotulo}</span>
      <span className={`font-medium tabular-nums ${negativo ? "text-erro" : ""}`}>
        {negativo ? "−" : ""}{fmt(valor)}
      </span>
    </span>
  );
}

export default function EquipeRelatorios() {
  const [dataInicio, setDataInicio] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [dataFim, setDataFim] = useState(format(new Date(), "yyyy-MM-dd"));
  const [servicoAberto, setServicoAberto] = useState(false);
  // Drill-down por bucket de cultura. Filtra só os 3 cards de baixo — a
  // composição do topo, o card de cultura e o radar seguem sendo do período
  // inteiro, senão a referência some justo quando se navega nela.
  const [culturaFiltro, setCulturaFiltro] = useState<string | null>(null);

  const diasDiff = differenceInDays(parseISO(dataFim), parseISO(dataInicio));
  const dias = diasDiff >= 0 ? diasDiff + 1 : 0;

  const { data, isLoading } = useQuery<RelatorioData>({
    queryKey: ["/api/equipe/relatorios", dataInicio, dataFim, culturaFiltro],
    queryFn: () =>
      fetchJson<RelatorioData>(
        `/api/equipe/relatorios?dataInicio=${dataInicio}&dataFim=${dataFim}` +
        (culturaFiltro ? `&culturaId=${encodeURIComponent(culturaFiltro)}` : ""),
      ),
    enabled: !!dataInicio && !!dataFim && dias > 0,
  });

  const mostrarMatriz = !!data && data.atividades.length > 0 && (data.produtivos.length > 0 || data.servico.length > 0);

  // I-R3: o servidor manda todo bucket conhecido, inclusive zerado. Esconder é
  // decisão daqui — assim o payload continua conferível e a tela continua limpa.
  const bucketsVisiveis = (data?.porCultura ?? []).filter(b => b.total > 0);
  const bucketFiltrado = culturaFiltro
    ? (data?.porCultura ?? []).filter(b => b.id === culturaFiltro)[0]
    : undefined;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        titulo="Relatórios"
        descricao={
          dias > 0
            ? `${format(parseISO(dataInicio), "dd/MM", { locale: ptBR })} a ${format(parseISO(dataFim), "dd/MM", { locale: ptBR })} · ${dias} dia${dias !== 1 ? "s" : ""}`
            : undefined
        }
      />

      <PageToolbar>
        <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="flex-[1_1_8rem] min-w-0 text-xs md:flex-none md:w-40" />
        <span className="text-muted-foreground text-sm">até</span>
        <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="flex-[1_1_8rem] min-w-0 text-xs md:flex-none md:w-40" />
      </PageToolbar>

      {isLoading ? (
        <div className="space-y-4">
          <PageSkeleton variante="painel" />
        </div>
      ) : !data ? null : (
        <>
          {/* Composição do custo. O número grande é o custo TOTAL do trabalho —
              não "o que sai no dia 5". Os chips existem justamente para que a
              diferença entre este total e o da folha seja explicável de bate-pronto. */}
          <div className="rounded-lg border bg-card p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Custo total no período</p>
                <p className="text-2xl font-bold tabular-nums">{fmt(data.totais.custoTotal)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Tarefas</p>
                <p className="text-2xl font-bold tabular-nums">{data.totalTarefas}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <ChipNatureza rotulo="Diárias" valor={data.totais.trabalho} />
              <ChipNatureza rotulo="Adicionais" valor={data.totais.adicionais} />
              <ChipNatureza rotulo="Mensalistas" valor={data.totais.fixoMensalistas} />
              <ChipNatureza rotulo="Descontos" valor={data.totais.descontos} negativo />
            </div>

            {data.mesCorrenteParcial && (
              <p className="text-xs text-atencao">
                O período vai além de hoje — o fixo dos mensalistas foi apurado só até hoje.
              </p>
            )}
          </div>

          {/* Escopo do drill-down, dito com todas as letras: o filtro pega os três
              cards de baixo e não pega o card de cultura nem o radar. Sem esta
              linha, "Por setor" encolhendo sozinho parece dado sumindo. */}
          {bucketFiltrado && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
              <span className="font-medium">Filtrando: {bucketFiltrado.nome}</span>
              <span className="text-xs text-muted-foreground">
                funcionário, atividade e setor mostram só esta fatia ({fmt(bucketFiltrado.total)})
              </span>
              <button
                type="button"
                onClick={() => setCulturaFiltro(null)}
                className="ml-auto rounded px-2 py-0.5 text-xs underline underline-offset-2 hover:bg-muted"
              >
                limpar ✕
              </button>
            </div>
          )}

          {/* 4 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Por cultura — o rateio contábil. Primeiro card de propósito: é a
                pergunta que a tela passou a responder. */}
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-semibold text-sm">Por cultura</p>
                <span className="text-[11px] text-muted-foreground">% entre culturas</span>
              </div>
              <div className="space-y-1.5">
                {bucketsVisiveis.map(b => {
                  const ativo = culturaFiltro === b.id;
                  const alerta = b.tipo === "SEM_CULTURA";
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setCulturaFiltro(ativo ? null : b.id)}
                      aria-pressed={ativo}
                      className={`w-full flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                        ativo ? "bg-primary/10 ring-1 ring-primary/30" : "hover:bg-muted/50"
                      }`}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className={`truncate ${alerta ? "text-atencao font-medium" : ""}`}>
                          {b.nome}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {fmt(b.total)} · {pctFmt(b.pctTotal)} do total
                        </span>
                      </span>
                      <span className="shrink-0 text-sm font-semibold tabular-nums">
                        {b.pctEntreCulturas === null ? "—" : pctFmt(b.pctEntreCulturas)}
                      </span>
                    </button>
                  );
                })}
                {bucketsVisiveis.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhum custo no período</p>
                )}
                {bucketsVisiveis.some(b => b.tipo === "SEM_CULTURA") && (
                  <p className="pt-1 text-[11px] text-atencao">
                    “Sem cultura” = setor produtivo sem variedade cadastrada. O custo é real; falta o cadastro.
                  </p>
                )}
              </div>
            </div>

            {/* Por funcionário */}
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <p className="font-semibold text-sm">Por funcionário</p>
              <div className="space-y-1.5">
                {data.porFuncionario.map(r => (
                  <div key={r.id} className="flex justify-between gap-3 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate">{r.nome}</span>
                      {/* O encarregado finalmente aparece com custo: o fixo dele
                          entra por custo-dia, nos setores onde ele de fato esteve. */}
                      {(r.fixo > 0 || r.adicionais > 0) && (
                        <span className="block text-xs text-muted-foreground tabular-nums">
                          {r.fixo > 0 && `fixo ${fmt(r.fixo)}`}
                          {r.fixo > 0 && r.adicionais > 0 && " · "}
                          {r.adicionais > 0 && `adicionais ${fmt(r.adicionais)}`}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 font-medium tabular-nums">{fmt(r.total)}</span>
                  </div>
                ))}
                {data.porFuncionario.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhum custo no período</p>
                )}
              </div>
            </div>

            {/* Por atividade */}
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <p className="font-semibold text-sm">Por atividade</p>
              <div className="space-y-1.5">
                {data.porAtividade.map(r => (
                  <div key={r.id} className="flex justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">{r.nome}</span>
                    <span className="shrink-0 font-medium tabular-nums">{fmt(r.total)}</span>
                  </div>
                ))}
                {data.porAtividade.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhuma tarefa com atividade no período</p>
                )}
              </div>
            </div>

            {/* Por setor */}
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <p className="font-semibold text-sm">Por setor</p>
              <div className="space-y-1.5">
                {data.porSetor.map(r => (
                  <div key={r.id} className="flex justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">{r.nome}</span>
                    <span className="shrink-0 font-medium tabular-nums">{fmt(r.total)}</span>
                  </div>
                ))}
                {data.porSetor.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhuma tarefa com setor no período</p>
                )}
              </div>
            </div>
          </div>

          {/* Matriz última atividade por setor — só setores PRODUTIVOS (radar de
              cobertura de manejo). Setor de serviço vai no rodapé expansível.
              Fora do filtro de cultura: o radar não é custo, é cobertura. */}
          {mostrarMatriz && (
            <DataCard
              titulo="Última vez que cada atividade foi feita em cada setor"
              rodape={
                <div className="flex flex-wrap gap-4">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sucesso inline-block" />Recente (≤7d)</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-atencao inline-block" />Atenção (8-14d)</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-erro inline-block" />Há mais de 14 dias</span>
                <span className="flex items-center gap-1"><span className="text-muted-foreground/40">—</span>Sem registro</span>
                  <span className="flex items-center gap-1"><span className="text-muted-foreground/25">·</span>Não se aplica</span>
                </div>
              }
            >
              {data.produtivos.length > 0 && (
                <div className="overflow-x-auto">
                  {/* Matriz de N atividades: sem `colunas`, porque o número de
                      colunas é dado, não layout — `DataTable` só aplica
                      `table-fixed` quando recebe `<colgroup>`. */}
                  <DataTable>
                    <DataTableHeader>
                      <DataTableRow>
                        <DataTableHead className="min-w-[80px]">Setor</DataTableHead>
                        {data.atividades.map(a => (
                          <DataTableHead key={a.id} className="min-w-[110px] whitespace-nowrap">{a.nome}</DataTableHead>
                        ))}
                      </DataTableRow>
                    </DataTableHeader>
                    <DataTableBody>
                      {data.produtivos.map(s => (
                        <DataTableRow key={s.setorId}>
                          <DataTableCell destaque>{s.setorNome}</DataTableCell>
                          {data.atividades.map(a => (
                            <DataTableCell key={a.id}>
                              <CelulaMatriz celula={s.celulas[a.id]} culturaSetor={s.culturaSetor} />
                            </DataTableCell>
                          ))}
                        </DataTableRow>
                      ))}
                    </DataTableBody>
                  </DataTable>
                </div>
              )}

              {/* §3.3 — antídoto do caso real: "Desbrota 368d atrás" em Outros era
                  lançamento errado, e sai da matriz sem sumir do mundo. */}
              {data.servico.length > 0 && (
                <div className="border-t bg-muted/10">
                  <button
                    type="button"
                    onClick={() => setServicoAberto(v => !v)}
                    className="w-full text-left px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {data.servico.length} área{data.servico.length > 1 ? "s" : ""} de serviço com registros de atividade{" "}
                    <span className="underline underline-offset-2">{servicoAberto ? "· ocultar" : "· ver"}</span>
                  </button>

                  {servicoAberto && (
                    <div className="px-4 pb-4 space-y-3">
                      <p className="text-xs text-muted-foreground">
                        <strong className="text-foreground">Áreas de serviço</strong> — trabalho registrado fora da
                        lavoura. Não entra no radar de manejo; serve para localizar lançamento em setor errado.
                      </p>
                      <div className="overflow-x-auto rounded-md border bg-card">
                        <DataTable>
                          <DataTableHeader>
                            <DataTableRow>
                              <DataTableHead className="min-w-[80px]">Setor</DataTableHead>
                              {data.atividades.map(a => (
                                <DataTableHead key={a.id} className="min-w-[100px] whitespace-nowrap">
                                  {a.nome}
                                </DataTableHead>
                              ))}
                            </DataTableRow>
                          </DataTableHeader>
                          <DataTableBody>
                            {data.servico.map(s => (
                              <DataTableRow key={s.setorId}>
                                <DataTableCell className="text-muted-foreground font-medium">{s.setorNome}</DataTableCell>
                                {data.atividades.map(a => {
                                  const cel = s.celulas[a.id];
                                  return (
                                    <DataTableCell key={a.id} className="text-muted-foreground">
                                      {cel?.estado === "COM_REGISTRO" ? `${diasAtras(cel.ultimaData)}d atrás` : "—"}
                                    </DataTableCell>
                                  );
                                })}
                              </DataTableRow>
                            ))}
                          </DataTableBody>
                        </DataTable>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DataCard>
          )}
        </>
      )}
    </div>
  );
}
